import { getAuth } from 'firebase/auth';

// URLs de vos Cloud Run Functions (us-central1)
// Format : https://{nom}-{hash}-uc.a.run.app
// Récupérez le hash exact dans GCP Console → Cloud Run → cliquez sur la fonction → copiez l'URL
const CLOUD_RUN_URLS: Record<string, string> = {
  'send-welcome-email':          import.meta.env.VITE_FN_SEND_WELCOME_EMAIL,
  'send-system-alert-email':     import.meta.env.VITE_FN_SEND_SYSTEM_ALERT,
  'send-payment-email':          import.meta.env.VITE_FN_SEND_PAYMENT_EMAIL,
  'send-document-email':         import.meta.env.VITE_FN_SEND_DOCUMENT_EMAIL,
  'send-deal-notification':      import.meta.env.VITE_FN_SEND_DEAL_NOTIFICATION,
  'create-seller-checkout':      import.meta.env.VITE_FN_SELLER_CHECKOUT,
  'create-buyer-checkout':       import.meta.env.VITE_FN_BUYER_CHECKOUT,
  'create-consultant-checkout':  import.meta.env.VITE_FN_CONSULTANT_CHECKOUT,
  'generatenda':                 import.meta.env.VITE_FN_GENERATE_NDA,
  'generateprofessionaldoc':     import.meta.env.VITE_FN_GENERATE_DOC,
  'analyzedocument':             import.meta.env.VITE_FN_ANALYZE_DOCUMENT,
  'translatetext':               import.meta.env.VITE_FN_TRANSLATE_TEXT,
  'getvoicetoken':               import.meta.env.VITE_FN_GET_VOICE_TOKEN,
  'makecall':                    import.meta.env.VITE_FN_MAKE_CALL,
  'endcall':                     import.meta.env.VITE_FN_END_CALL,
  'savesignednda':               import.meta.env.VITE_FN_SAVE_SIGNED_NDA,
  'stripewebhook':               import.meta.env.VITE_FN_STRIPE_WEBHOOK,
  'transcribe-meeting':          import.meta.env.VITE_FN_TRANSCRIBE_MEETING,
  'generate-meeting-summary':    import.meta.env.VITE_FN_GENERATE_MEETING_SUMMARY,
  'get-public-staff-availability': import.meta.env.VITE_FN_GET_PUBLIC_STAFF_AVAILABILITY,
  'book-staff-call':             import.meta.env.VITE_FN_BOOK_STAFF_CALL,
};

interface InvokeFunctionOptions {
  requireAuth?: boolean; // true par défaut
  method?: 'POST' | 'GET';
}

export interface InvokeFunctionResult<T = any> {
  data: T | null;
  error: Error | null;
}

/**
 * Appelle une Cloud Run Function par son nom.
 * Gère automatiquement le token Firebase Auth.
 *
 * @param functionName - Nom de la fonction (clé dans CLOUD_RUN_URLS)
 * @param body - Corps de la requête
 * @param options - Options (auth, méthode)
 */
export async function invokeFunction<T = any>(
  functionName: string,
  body?: Record<string, any>,
  options: InvokeFunctionOptions = {}
): Promise<T | any> {
  const { requireAuth = true, method = 'POST' } = options;

  const url = CLOUD_RUN_URLS[functionName];
  if (!url) {
    console.error(`[cloudrun] No URL configured for function: ${functionName}`);
    throw new Error(`Function URL not configured: ${functionName}`);
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (requireAuth) {
    const currentUser = getAuth().currentUser;
    if (!currentUser) {
      throw new Error('Not authenticated');
    }
    const token = await currentUser.getIdToken();
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Function ${functionName} returned ${response.status}: ${errorText}`);
  }

  return await response.json().catch(() => null);
}

/**
 * Version "fire-and-forget" — pour les emails et notifications.
 * Les erreurs sont loggées mais ne bloquent pas le flux.
 */
export async function invokeFunctionSilent(
  functionName: string,
  body?: Record<string, any>,
  options: InvokeFunctionOptions = {}
): Promise<void> {
  try {
    await invokeFunction(functionName, body, options);
  } catch (err: any) {
    console.error(`[cloudrun] Silent call failed for ${functionName}:`, err?.message || err);
  }
}